import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "../../../store/globalState";
import { FirstSendWarning } from "../../../components/first-send/FirstSendWarning";
import { useFirstSendWarning } from "../../../hooks/useFirstSendWarning";
import { shouldPromptPhone } from "../../../utils/phoneGate";
import { ensureSmsConsent } from "../../../components/notifications/smsConsent";
import { useSessionStore } from "../../../store/useSessionStore";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { BodyCopy } from "../../../components/BodyCopy";
import {
  AuraLayout,
  BankIcon,
  Button,
  CashSpecialIcon,
  Layout,
  WalletIcon,
  WarningIcon,
} from "lola-framework-ui-test";
import { APP_ROUTES, flavorClassName } from "../../../constants";
import { FlagCircle } from "../../../components/FlagCircle";
import { QuoteInfo } from "./components/QuoteInfo";
import { amountFormatter } from "../../../utils";
import { formatBankAccountLabel } from "../../../utils/bankAccountLabel";
import { ExchangeFeeInfo } from "./components/ExchangeFeeInfo";
import { PayoutInfo } from "./components/PayoutInfo";
import { Popup } from "../../../components/Popup";
import { ScrollFadeIndicator } from "../../../components/ScrollFadeIndicator";
import { SelectPaymentMethodSlot } from "./components/SelectPaymentMethodSlot";
import { DestinationAccountInfo } from "./components/DestinationAccountInfo";
import { paymentType } from "../../../interfaces/payment";
import type { ICardPaymentMethod } from "../../../interfaces/payment-method";
import masterCardLogo from "../../../assets/master_card_icon.svg";
import visaLogo from "../../../assets/visa_color_icon.svg";
import { useTransferPricing } from "../../../hooks/useTransferPricing";
import { getEvents, safeCapture } from "../../../analytics";
import { quoteMatchesSelection } from "../../../utils/quoteMatchesSelection";
import { useQuoteLoadingTimedOut } from "../../../hooks/useQuoteLoadingTimedOut";
import { checkCipReadiness, getAllPaymentMethods, getWidgetApiError, isDeadLinkError } from "../../../services/api";
import { categoryFromFundingMethod } from "../../../utils/catalog";
import {
  isCardFundingAvailable,
  unavailableCardReason,
} from "../../../utils/card-funding-availability";
import { UnavailableFundingCardSheet } from "../../../components/UnavailableFundingCardSheet";
import { Navbar } from "../../../components/Navbar";

export const TransferPage = memo(() => {
  const { t, i18n } = useTranslation();
  const { theme, amountState, paymentState, setPaymentState, setQuoteState, cardList } = useStore();
  const { session } = useSessionStore();
  const userStatus = session.data?.userStatus;
  const canSend = !userStatus?.blocked && !userStatus?.suspended;
  const { amount: amountRaw } = amountState;
  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, title, bodyCopy } = colorConfig;
  const navigate = useNavigate();
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [showDestinationPopup, setShowDestinationPopup] = useState(false);
  const [showExcratePopup, setShowExcratePopup] = useState(false);
  const [shakePayment, setShakePayment] = useState(false);
  const [cipLoading, setCipLoading] = useState(false);
  const [unavailableCard, setUnavailableCard] =
    useState<ICardPaymentMethod | null>(null);
  const unavailableGuardKeyRef = useRef<string | null>(null);

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("route_amount"));
    safeCapture(getEvents().SCREEN_VIEW("summary"));
  }, []);

  // Pricing: pre-quote (scoped to the claim's fixed payout) + binding quote.
  const {
    route: { sourceCountry, destinationCountry, sourceCurrency, destinationCurrency },
    availableCategories,
    availableFundingMethods,
    quote,
    quoteLoading,
    quoteError,
    refetchQuote,
    preQuoteLoading,
    economics,
    amount: pricedAmount,
    anchor: amountAnchor,
    selectedFundingMethod,
    capabilityKey,
  } = useTransferPricing();
  const amount = amountRaw;
  const quoteMatches = quoteMatchesSelection(quote, quoteLoading, {
    fundingMethod: selectedFundingMethod,
    capabilityKey,
  });
  const quoteLoadingTimedOut = useQuoteLoadingTimedOut(quoteLoading);
  const quoteFailed = Boolean(quoteError) || quoteLoadingTimedOut;
  const skuMissing =
    !preQuoteLoading && !!paymentState.payment && !capabilityKey;
  const firstSend = useFirstSendWarning({
    linkId: session.data?.linkId,
    sendAmount: economics?.sendAmount ?? quote?.sendAmount ?? null,
    sourceCurrency,
    fundingMethod: selectedFundingMethod,
  });
  const blockForPricing =
    Boolean(pricedAmount) &&
    Boolean(paymentState.payment) &&
    !quoteMatches &&
    !quoteFailed &&
    !skuMissing;
  const showQuoteLoading =
    (quoteLoading || blockForPricing) && !quoteFailed && !skuMissing;

  // Auto-select last used funding method on mount
  useEffect(() => {
    if (paymentState.payment) return;
    const linkId = session.data?.linkId;
    if (!linkId) return;

    getAllPaymentMethods(linkId)
      .then((methods) => {
        if (paymentState.payment) return;
        const lastUsed = methods.lastUsedFunding;
        if (!lastUsed) return;

        const lastCategory = categoryFromFundingMethod(lastUsed.method);

        if (lastCategory === "CASH" && availableCategories.has("CASH")) {
          setPaymentState({
            payment: {
              cardId: null,
              type: paymentType.CASH,
              brand: t("payment.cashDeposit"),
            },
          });
        } else if (
          lastCategory === "CARD" &&
          lastUsed.paymentMethodId &&
          availableCategories.has("CARD")
        ) {
          const card = methods.cards.find(
            (c) => c.enabled && c.paymentMethodId === lastUsed.paymentMethodId
          );
          if (card && isCardFundingAvailable(card, availableFundingMethods)) {
            setPaymentState({
              payment: {
                cardId: card.paymentMethodId,
                type: paymentType.CARD,
                brand: `${card.brand}-${card.last4}`,
              },
            });
          }
        }
      })
      .catch(() => {});
  }, [
    session.data?.linkId,
    cardList,
    availableCategories,
    availableFundingMethods,
    paymentState.payment,
    setPaymentState,
    t,
  ]);

  // Clear a selected card that cannot fund the current quoted methods.
  useEffect(() => {
    const payment = paymentState.payment;
    if (!payment || payment.type !== paymentType.CARD || !payment.cardId) {
      unavailableGuardKeyRef.current = null;
      return;
    }
    if (preQuoteLoading) return;
    const linkId = session.data?.linkId;
    if (!linkId) return;

    const key = `${payment.cardId}|${[...availableFundingMethods]
      .sort()
      .join(",")}`;
    if (unavailableGuardKeyRef.current === key) return;

    let cancelled = false;
    getAllPaymentMethods(linkId)
      .then((methods) => {
        if (cancelled) return;
        const card = methods.cards.find(
          (c) => c.enabled && c.paymentMethodId === payment.cardId
        );
        if (!card) return;
        if (isCardFundingAvailable(card, availableFundingMethods)) {
          unavailableGuardKeyRef.current = key;
          return;
        }
        unavailableGuardKeyRef.current = key;
        const reason = unavailableCardReason(card, availableFundingMethods);
        safeCapture(getEvents().CARD_UNAVAILABLE_SHOWN, { reason });
        setPaymentState({ payment: null });
        setUnavailableCard(card);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [
    paymentState.payment,
    availableFundingMethods,
    preQuoteLoading,
    session.data?.linkId,
    setPaymentState,
  ]);

  useEffect(() => {
    if (quoteError) {
      navigate(APP_ROUTES.SERVICE_UNAVAILABLE_PAGE, { replace: true });
    }
  }, [quoteError, navigate]);

  // Handle click on confirm button - check CIP readiness before proceeding
  const handleConfirmClick = useCallback(async () => {
    safeCapture(getEvents().CONTINUE_CLICK);
    if (!canSend) {
      navigate(APP_ROUTES.TRANSACTIONS_PAGE, { replace: true });
      return;
    }
    if (!paymentState.payment) {
      setShakePayment(true);
      setTimeout(() => setShakePayment(false), 500);
      return;
    }
    if (!amount || !quoteMatches || !quote) {
      return;
    }

    const linkId = session.data?.linkId;
    if (!linkId) {
      console.error("[TransferPage] No linkId found in session");
      return;
    }

    if (
      paymentState.payment.type === paymentType.CARD &&
      paymentState.payment.cardId
    ) {
      try {
        const methods = await getAllPaymentMethods(linkId);
        const card = methods.cards.find(
          (c) =>
            c.enabled && c.paymentMethodId === paymentState.payment?.cardId
        );
        if (!card || !isCardFundingAvailable(card, availableFundingMethods)) {
          if (card) {
            const reason = unavailableCardReason(
              card,
              availableFundingMethods
            );
            safeCapture(getEvents().CARD_UNAVAILABLE_SHOWN, { reason });
            setUnavailableCard(card);
          } else {
            setShakePayment(true);
            setTimeout(() => setShakePayment(false), 500);
          }
          return;
        }
      } catch (err) {
        if (isDeadLinkError(err)) return;
        console.error(
          "[TransferPage] Failed to validate funding card before confirm:",
          err
        );
      }
    }

    // DEBUG: Log quote info before storing
    const expiresAt = new Date(quote.expiresAt).getTime();
    const now = Date.now();
    const remainingMs = expiresAt - now;
    console.log("[TransferPage] Storing quote before navigation:", {
      quoteId: quote.quoteId,
      expiresAt: quote.expiresAt,
      remainingSeconds: Math.floor(remainingMs / 1000),
      isExpired: remainingMs <= 0,
      quoteLoading,
    });

    // Store quote in global state before navigating
    setQuoteState(quote);

    // Check CIP readiness before proceeding to transfer
    try {
      setCipLoading(true);
      console.log("[TransferPage] Checking CIP readiness...");
      const cipResult = await checkCipReadiness(linkId);
      console.log("[TransferPage] CIP readiness result:", cipResult);

      if (cipResult.shouldRunCip && cipResult.cipTicketId) {
        // User needs to complete CIP - navigate to embedded Kentucky SDK page
        console.log(
          "[TransferPage] CIP required, navigating to CIP page with ticketId:",
          cipResult.cipTicketId
        );
        navigate(APP_ROUTES.CIP_PAGE, {
          replace: true,
          state: { cipTicketId: cipResult.cipTicketId },
        });
        return;
      }

      const prompt = shouldPromptPhone(session.data, linkId);
      if (prompt) {
        navigate(APP_ROUTES.PHONE_PAGE, {
          replace: true,
          state: { next: APP_ROUTES.SENDING_MONEY_PAGE },
        });
        return;
      }
      await ensureSmsConsent(linkId, i18n.language?.slice(0, 2) || "en");
      navigate(APP_ROUTES.SENDING_MONEY_PAGE, {
        replace: true,
        state: { next: APP_ROUTES.SENDING_MONEY_PAGE },
      });
    } catch (err) {
      console.error("[TransferPage] CIP readiness check failed:", err);
      // Link expired / revoked: the dead-link handler already routed to the
      // screen that explains why the link stopped working.
      if (isDeadLinkError(err)) {
        return;
      }
      const apiCode = getWidgetApiError(err)?.code;
      if (apiCode === "USER_BLOCKED" || apiCode === "USER_SUSPENDED") {
        navigate(APP_ROUTES.ERROR_PAGE, {
          replace: true,
          state: {
            title: t("error.transfersDisabledTitle"),
            subTitle: null,
            message: t("error.transfersDisabledMessage"),
            btnText: t("common.needHelp"),
            isOut: false,
            pageUrl: APP_ROUTES.SUPPORT_PAGE,
          },
        });
        return;
      }
      // On error, navigate to error page
      navigate(APP_ROUTES.ERROR_PAGE, {
        state: {
          title: t("transfer.error"),
          subTitle: t("transfer.errorSubtitle", "Something went wrong"),
          message:
            err instanceof Error
              ? err.message
              : "Failed to check CIP readiness",
          btnText: t("transfer.tryAgain", "Try again"),
          isOut: false,
          pageUrl: APP_ROUTES.TRANSFER_PAGE,
        },
      });
    } finally {
      setCipLoading(false);
    }
  }, [
    canSend,
    paymentState.payment,
    amount,
    quote,
    quoteMatches,
    session.data?.linkId,
    setQuoteState,
    navigate,
    quoteLoading,
    availableFundingMethods,
    t,
    i18n,
  ]);

  // Display economics: binding quote when available, else the pre-quote estimate.
  const exchangeRate = economics?.exchangeRate ?? 0;
  const fee = economics?.fee ?? 0;

  // Derived opposite-leg displays for the non-pinned QuoteInfo input.
  const receiveFormatted = useMemo(() => {
    if (quoteLoading) return "...";
    if (economics?.receiveAmount) {
      return amountFormatter(economics.receiveAmount, destinationCurrency, {
        country: "en-US",
      });
    }
    return "";
  }, [economics, quoteLoading, destinationCurrency]);

  const sendFormatted = useMemo(() => {
    if (quoteLoading) return "...";
    if (economics?.sendAmount != null) {
      return amountFormatter(economics.sendAmount, sourceCurrency, {
        country: "en-US",
      });
    }
    return "";
  }, [economics, quoteLoading, sourceCurrency]);

  const totalPayment = useMemo(() => {
    if (economics?.totalCost) {
      return amountFormatter(economics.totalCost);
    }
    if (amount) {
      return amountFormatter(parseFloat(amount) + fee);
    }
    return "0";
  }, [economics, amount, fee]);

  const feeDisplay = useMemo(() => {
    return `$${fee.toFixed(2)}`;
  }, [fee]);

  const pickIcon = useCallback((type: paymentType, brand: string) => {
    switch (type) {
      case "CARD": {
        let finalIcon = null;
        if (brand.toLocaleUpperCase().includes("VISA")) finalIcon = visaLogo;
        if (brand.toLocaleUpperCase().includes("MASTERCARD"))
          finalIcon = masterCardLogo;

        return {
          brand,
          icon: <img src={finalIcon ?? ""} alt={`${brand} logo`} />,
        };
      }
      case "BANK":
        return {
          brand,
          icon: <BankIcon size={20} />,
        };
      case "CASH":
        return {
          brand,
          icon: <CashSpecialIcon size={20} />,
        };

      default:
        return {
          brand,
          icon: <WalletIcon size={20} />,
        };
    }
  }, []);

  const paymentSelected = useMemo(
    () =>
      paymentState.payment
        ? pickIcon(paymentState.payment.type, paymentState.payment.brand)
        : {
            brand: t("transfer.selectPaymentMethod"),
            icon: <WalletIcon size={20} />,
          },
    [paymentState, pickIcon, t]
  );

  const payoutDisplay = useMemo(() => {
    const pm = session.data?.userData?.paymentMethod;
    if (!pm) {
      return {
        subtitle: t("transfer.bankAccount", { last4: "****" }),
        icon: <BankIcon size={20} />,
      };
    }
    if (pm.type === "bank_account" && pm.bankAccount) {
      const digits = (pm.bankAccount.accountNumber ?? "")
        .replace(/\D/g, "")
        .slice(-4);
      const label =
        formatBankAccountLabel(
          pm.bankAccount.bankName,
          pm.bankAccount.accountNumber,
        ) ??
        (digits
          ? t("transfer.bankAccount", { last4: digits })
          : pm.bankAccount.accountNumber);
      return { subtitle: label, icon: <BankIcon size={20} /> };
    }
    if (pm.type === "card" && pm.card) {
      const brand = pm.card.brand?.toUpperCase() || "CARD";
      return {
        // Card label parity: BRAND-0000.
        subtitle: `${brand}-${pm.card.last4}`,
        icon: <WalletIcon size={20} />,
      };
    }
    if (pm.type === "cash") {
      const label = pm.cash?.provider || t("transfer.cashPickup", "Cash pickup");
      return { subtitle: label, icon: <CashSpecialIcon size={20} /> };
    }
    return {
      subtitle: t("transfer.bankAccount", { last4: "****" }),
      icon: <BankIcon size={20} />,
    };
  }, [session.data?.userData?.paymentMethod, t]);

  if (!canSend) {
    navigate(APP_ROUTES.TRANSACTIONS_PAGE, { replace: true });
    return null;
  }

  return (
    <>
      <AuraLayout 
        colorConfig={colorConfig}
        className={`scrollable white-view-background ${flavorClassName}`}
      >
        <Layout.Header>
          <Navbar
            title={t("transfer.title")}
            color={title}
            align="center"
            onBackClick={() => navigate(-1)}
          />
        </Layout.Header>
        <Layout.Content className="transfer-section">
          <ScrollFadeIndicator color={bodyCopy} />
          <section>
            <section className="estimated-quote-section">
              <section className="quote-container">
                <QuoteInfo
                  label={t("transfer.from")}
                  currency={sourceCurrency}
                  textColor={bodyCopy}
                  mode="input"
                  side="send"
                  isActive={amountAnchor === "send"}
                  derivedAmount={
                    amountAnchor === "receive" ? sendFormatted || undefined : undefined
                  }
                  icon={<FlagCircle iso2={sourceCountry.toLowerCase()} />}
                />
                <QuoteInfo
                  label={t("transfer.to")}
                  currency={destinationCurrency}
                  textColor={bodyCopy}
                  mode="input"
                  side="receive"
                  isActive={amountAnchor === "receive"}
                  derivedAmount={
                    amountAnchor === "send" ? receiveFormatted || undefined : undefined
                  }
                  hideCurrencySymbol
                  icon={<FlagCircle iso2={destinationCountry.toLowerCase()} />}
                />
                <ExchangeFeeInfo
                  fee={quoteLoading ? "..." : exchangeRate.toFixed(4)}
                  fromCurrency={sourceCurrency}
                  currency={destinationCurrency}
                  onClick={() => setShowExcratePopup(true)}
                />
              </section>
            </section>
            <section className="payout-container">
              <PayoutInfo
                title={t("transfer.payingWith")}
                subtitle={paymentSelected.brand}
                icon={paymentSelected.icon}
                background={backgroundBtn}
                color={textColorBtn}
                onChange={() => setShowPaymentPopup(true)}
                className={`${
                  !paymentState.payment ? "payment-required" : ""
                } ${shakePayment ? "shake" : ""}`.trim()}
              />
              <PayoutInfo
                title={t("transfer.receiveIn")}
                subtitle={payoutDisplay.subtitle}
                icon={payoutDisplay.icon}
                background={backgroundBtn}
                color={textColorBtn}
                onChange={() => setShowDestinationPopup(true)}
              />
              {skuMissing ? (
                <BodyCopy className="payment-error-text" role="alert">
                  {t("transfer.paymentMethodUnavailable")}
                </BodyCopy>
              ) : null}
              {quoteFailed && !skuMissing ? (
                <>
                  <BodyCopy className="payment-error-text" role="alert">
                    {t("transfer.quoteError")}
                  </BodyCopy>
                  <Button
                    variant="cancel"
                    size="small"
                    color={bodyCopy}
                    onClick={() => {
                      void refetchQuote();
                    }}
                  >
                    {t("transfer.quoteRetry")}
                  </Button>
                </>
              ) : null}
              {firstSend.visible ? (
                <FirstSendWarning title={firstSend.title} body={firstSend.body} />
              ) : null}
              <p className="footer-text foreground transfer-timing-note">
                <b>{t("transfer.instantTransfer")}</b>
                {" "}
                <span className="transfer-timing-note-rest">
                  {t("transfer.mayTakeFew")}
                </span>
              </p>
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <section className="footer-summary">
              <p className="footer-text foreground footer-summary-title">
                <b>{t("transfer.detail")}</b>
              </p>
              <p className="footer-text foreground">
                {t("transfer.fee", {
                  amount: feeDisplay,
                  currency: sourceCurrency,
                })}
              </p>
              <p className="footer-text foreground">
                {t("transfer.totalPayment", {
                  amount: `$${totalPayment}`,
                  currency: sourceCurrency,
                })}
              </p>
            </section>
            <div
              className={`button-wrapper ${
                (amount && paymentState.payment && quoteMatches) || cipLoading
                  ? "rainbow-ready"
                  : ""
              }`}
            >
              <Button
                size="small"
                background={backgroundBtn}
                color={textColorBtn}
                disabled={cipLoading || blockForPricing || quoteFailed}
                onClick={handleConfirmClick}
              >
                {cipLoading
                  ? t("transfer.verifying", "Verifying...")
                  : showQuoteLoading
                  ? t("transfer.loadingQuote", "Loading...")
                  : t("transfer.confirmTransfer")}
              </Button>
            </div>
          </section>
        </Layout.Footer>
      </AuraLayout>
      <Popup
        visible={showPaymentPopup}
        onClose={() => setShowPaymentPopup(false)}
      >
        <SelectPaymentMethodSlot onClose={() => setShowPaymentPopup(false)} />
      </Popup>
      {unavailableCard ? (
        <Popup
          visible
          onClose={() => setUnavailableCard(null)}
        >
          <UnavailableFundingCardSheet
            card={unavailableCard}
            available={availableFundingMethods}
            bodyCopy={bodyCopy}
            backgroundBtn={backgroundBtn}
            textColorBtn={textColorBtn}
            showHeaderClose={false}
            onChangeMethod={() => {
              setUnavailableCard(null);
              setShowPaymentPopup(true);
            }}
            onDismiss={() => setUnavailableCard(null)}
          />
        </Popup>
      ) : null}
      <Popup
        visible={showDestinationPopup}
        onClose={() => setShowDestinationPopup(false)}
      >
        <DestinationAccountInfo
          onClose={() => setShowDestinationPopup(false)}
        />
      </Popup>
      <Popup
        visible={showExcratePopup}
        onClose={() => setShowExcratePopup(false)}
      >
        <section className="qoute-popup">
          <WarningIcon size={60} />
          <BodyCopy textColor={bodyCopy}>
            <Trans i18nKey="transfer.quote.disclaimer" />
          </BodyCopy>
        </section>
      </Popup>
    </>
  );
});
